import React, { createContext, useContext } from "react"
import { useCurrentUser, useLogout, useRequestPasswordReset } from "@/lib/queries"
import type { User } from "@/lib/types"

interface AuthContextValue {
  user: User | null | undefined
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => void
  requestPasswordReset: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading, isError } = useCurrentUser()
  const logoutMutation = useLogout()
  const resetMutation = useRequestPasswordReset()

  const value: AuthContextValue = {
    user: isError ? null : user,
    isAuthenticated: !!user && !isError,
    isLoading,
    logout: () => logoutMutation.mutate(),
    requestPasswordReset: () => resetMutation.mutate(),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
